import React, { FC, useEffect, useState } from "react";
import { getFormValues, change, touch } from "@mds/common/components/forms/form";
import { Row, Button, Steps } from "antd";
import * as FORM from "@/constants/forms";
import FormWrapper from "@mds/common/components/forms/FormWrapper";
import RenderCancelButton from "@mds/common/components/forms/RenderCancelButton";
import RenderSubmitButton from "@mds/common/components/forms/RenderSubmitButton";
import {
  createComplianceCode,
  formatCode,
  getActiveComplianceCodesList,
} from "@mds/common/redux/slices/complianceCodesSlice";
import { closeModal } from "@mds/common/redux/actions/modalActions";
import { HSRCEditForm } from "./HSRCEditForm";
import { ReportEditForm } from "./ReportEditForm";
import CoreButton from "@mds/common/components/common/CoreButton";
import { IComplianceArticle } from "@mds/common/interfaces";
import { REPORT_REGULATORY_AUTHORITY_CODES } from "@mds/common/constants/enums";
import { useAppDispatch, useAppSelector } from "@mds/common/redux/rootState";

const ComplianceCodeViewEditForm: FC<{
  initialValues: IComplianceArticle;
  isEditMode: boolean;
  onSave: (values: IComplianceArticle) => void | Promise<void>;
}> = ({ initialValues = {}, isEditMode = true, onSave = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const dispatch = useAppDispatch();
  const complianceCodes = useAppSelector(getActiveComplianceCodesList);
  const formValues = useAppSelector(getFormValues(FORM.ADD_COMPLIANCE_CODE)) as IComplianceArticle;
  const { section, sub_section, paragraph, sub_paragraph } = formValues ?? {};

  const generateArticleNumber = () => {
    const articleNumber = [section, sub_section, paragraph, sub_paragraph]
      .filter(Boolean)
      .join(".");
    dispatch(change(FORM.ADD_COMPLIANCE_CODE, "articleNumber", articleNumber));
    dispatch(touch(FORM.ADD_COMPLIANCE_CODE, "articleNumber"));
  };

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const previous = () => {
    setCurrentStep(currentStep - 1);
  };

  useEffect(() => {
    if (section) {
      generateArticleNumber();
    }
  }, [section, sub_section, paragraph, sub_paragraph]);

  const steps = [
    {
      title: "HSRC Details",
      content: <HSRCEditForm complianceCodes={complianceCodes} />,
    },
    {
      title: "Report Details",
      content: (
        <ReportEditForm isEditMode={isEditMode}></ReportEditForm>
      ),
    },
  ];

  const handleSubmit = async (values: IComplianceArticle) => {
    if (currentStep === steps.length - 1) {
      const cim_or_cpo =
        values.cim_or_cpo !== REPORT_REGULATORY_AUTHORITY_CODES.NONE ? values.cim_or_cpo : null;
      const payload = { ...values, article_act_code: "HSRCM", cim_or_cpo };
      dispatch(createComplianceCode(payload)).then((resp) => {
        if (resp.payload) {
          if (onSave) {
            onSave(formatCode(resp.payload));
          }
          dispatch(closeModal());
        }
      });
    } else {
      next();
    }
  };

  return (
    <div>
      <FormWrapper
        name={FORM.ADD_COMPLIANCE_CODE}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isEditMode={isEditMode}
        isModal={true}
        forceRedux={true}
      >
        <Steps current={currentStep}>
          {steps.map((step) => (
            <Steps.Step key={step.title} title={step.title} />
          ))}
        </Steps>
        {steps[currentStep].content}
        <Row style={{ width: "100%" }} justify="end">
          <RenderCancelButton />
          {currentStep > 0 && (
            <CoreButton
              className="full-mobile"
              type="filled-tertiary"
              onClick={previous}
              disabled={false}
            >
              Back
            </CoreButton>
          )}

          {isEditMode ? (
            <RenderSubmitButton
              buttonText={currentStep === steps.length - 1 ? "Save Code" : "Continue"}
            />
          ) : (
            ""
          )}
          {!isEditMode && currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={next}>
              Continue
            </Button>
          ) : (
            ""
          )}
        </Row>
      </FormWrapper>
    </div>
  );
};

export default ComplianceCodeViewEditForm;
